/*
  Kiwi Ears · title tiles — Figma 412:1348 (Rene 2026-08-21).

  Model photos appear in the hero title BESIDE the series name — never on top of
  it. The name itself is never touched or split. Tiles come and go on their own
  timing (no hover), more than one can be on screen at once, and the rest of the
  title animates apart and back together to make room.

  Markup contract — the title carries data-titletiles and, optionally, a
  pipe-separated data-tile-photos pool; its direct children are .klr-part (text)
  and .klr-tile (a photo SLOT), in reading order:

    <h1 data-titletiles data-tile-photos="a.jpg|b.jpg|c.jpg">
      <span class="klr-part">The</span>
      <span class="klr-tile"><img …></span>     <!-- slot, left of the name -->
      <span class="klr-part">SOLO</span>
      <span class="klr-tile"><img …></span>     <!-- slot, right of the name -->
      <span class="klr-part">Series</span>
    </h1>

  A slot is not tied to a photo. With a pool present, each slot is filled with a
  random unused photo just before it pops, so no picture is stuck on one side.

  How the spacing works, and why it is done this way:
   · The tiles sit in NORMAL FLOW, so the title is always laid out at its widest.
     A hidden tile is scaled to 0 and the items after it are pulled left by its
     width plus the row gap, so the gap looks closed. Everything then gets half
     the total collapsed width back, which keeps the centred title centred.
   · That makes every state a pure TRANSFORM. Animating tile widths would relayout
     the whole title on every frame; this never touches layout after first paint.
   · A TILE NEVER TRAVELS WHILE IT IS VISIBLE (Rene, 2026-08-21). The recentring
     alone would drag the arriving tile half its own width, which reads as the
     photo sliding in from the side. Instead it is repositioned while invisible
     and scales from its own centre; the words do all the moving.
   · OPENING is 0.2s on expo.out — a pop, not a transition (Rene, 2026-08-21).
     The words part first and the photo pops into the gap 0.12s later, also in
     0.2s. Reversing that order (or overlapping them fully) puts the photo on
     top of a letter for a beat, because the arriving tile is already parked at
     its final position — see the note about travel below.
   · CLOSING keeps the long elastic.out(0.55, 0.45): the soft wobble as the words
     come back together. The tile itself leaves in 0.2s on power2.in, so the
     photo is gone before the wobble plays out and never rubber-bands.
   · Every tween is overwrite:"auto", so a tile arriving mid-settle retargets from
     wherever the words currently are instead of snapping.
   · Each tile keeps its OWN random schedule (gsap.delayedCall + gsap.utils.random)
     rather than sharing a timeline, so the popping never falls into a pattern.
     Concurrency is capped two ways: never more than MAX_VISIBLE (2) photos on
     screen at any instant, and never more than actually FITS the row (see
     capacity()) — which is one on a narrow window or behind a long series name.
     A tile whose turn lands while either cap is met simply rolls again.
   · Reduced motion shows two tiles, statically, as the design does. Coarse
     pointers and small screens skip it — the tiles are a desktop flourish.
*/
(function () {
  function init() {
    var gsap = window.gsap;
    if (!gsap) return;

    var roots = [].slice.call(document.querySelectorAll('[data-titletiles]'));
    if (!roots.length) return;

    var mm = gsap.matchMedia();

    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', function () {
      var stops = roots.map(function (root) { return run(root); });
      return function () { stops.forEach(function (s) { s && s(); }); };
    });

    mm.add('(min-width: 1024px) and (prefers-reduced-motion: reduce)', function () {
      var stops = roots.map(function (root) { return still(root); });
      return function () { stops.forEach(function (s) { s && s(); }); };
    });

    /* ---------- shared geometry ---------- */
    function build(root) {
      var items = [].slice.call(root.children);
      var tiles = items.filter(function (el) { return el.classList.contains('klr-tile'); });
      return {
        items: items,
        tiles: tiles,
        gap: function () { return parseFloat(getComputedStyle(root).columnGap) || 0; },
        /* x target per item, given which tiles are on. `changed` is the tile
           whose state is flipping — it is handled differently, see below. */
        place: function (open, opening, changed) {
          var gap = this.gap();
          var before = [];
          var collapsed = 0;
          this.items.forEach(function (el) {
            before.push(collapsed);
            if (el.classList.contains('klr-tile') && open.indexOf(el) < 0) {
              collapsed += el.offsetWidth + gap;
            }
          });
          var centre = collapsed / 2;
          /* Closing waits a beat so the words don't sweep through the tile
             while it is still shrinking. */
          var move = opening
            ? { duration: 0.2, ease: 'expo.out', delay: 0 }
            : { duration: 0.95, ease: 'elastic.out(0.55, 0.45)', delay: 0.2 };
          this.items.forEach(function (el, i) {
            var target = centre - before[i];
            var isTile = el.classList.contains('klr-tile');
            var isOpen = open.indexOf(el) > -1;

            /* A tile that is not open must never slide: it is either invisible
               (so its position is free) or still fading out, and sliding a
               fading tile drags it straight through the words. Hold, then snap
               once it is gone. */
            if (isTile && !isOpen) {
              gsap.killTweensOf(el, 'x');
              gsap.to(el, { x: target, duration: 0, delay: 0.2 });
              return;
            }
            /* The tile that is opening: the recentring would drag its own box
               half its width, which reads as the photo flying in from the side
               instead of popping. It is still invisible here, so give it its
               final x outright and let only the scale animate.
               killTweensOf first: overwrite:'auto' clears only tweens that have
               begun rendering, so a pending snap-back from an earlier close
               would survive and later throw the tile on top of a word. */
            if (el === changed) {
              gsap.killTweensOf(el, 'x');
              gsap.set(el, { x: target });
              return;
            }
            gsap.to(el, {
              x: target, delay: move.delay,
              duration: move.duration, ease: move.ease, overwrite: 'auto'
            });
          });
          this.tiles.forEach(function (t) {
            var on = open.indexOf(t) > -1;
            gsap.to(t, {
              scale: on ? 1 : 0,
              autoAlpha: on ? 1 : 0,
              duration: 0.2,
              /* An arriving tile waits for the gap: it is already sitting at
                 its final x, so popping before the words have moved would put
                 the photo on top of a letter. The wait is the full 0.2s of the
                 word tween — anything shorter breaks when the words start from
                 mid-flight (an open interrupting a close), where "nearly there"
                 can still be most of a tile away. */
              delay: (t === changed && on) ? 0.2 : 0,
              ease: on ? 'expo.out' : 'power2.in',
              overwrite: 'auto'
            });
          });
        }
      };
    }

    /* ---------- the ambient cycle ---------- */
    function run(root) {
      var g = build(root);
      if (!g.tiles.length) return null;
      gsap.set(g.tiles, { scale: 0, autoAlpha: 0, transformOrigin: '50% 50%' });

      /* The photo POOL. A tile in the markup is a SLOT, not a picture: a photo has
         to be able to turn up on either side of the name, so the side comes from
         which slot's timer fires and the picture comes from a separate draw here.
         Preloaded, because a slot is filled 0.2s before it is seen. */
      var pool = (root.getAttribute('data-tile-photos') || '')
        .split('|').filter(function (u) { return u; });
      pool.forEach(function (src) { var im = new Image(); im.src = src; });

      function fill(tile) {
        if (pool.length < 2) return;         /* nothing to shuffle */
        var img = tile.querySelector('img');
        if (!img) return;
        /* never the same photo twice on screen — exclude what other slots hold
           while they are open or still fading */
        var taken = [];
        g.tiles.forEach(function (t) {
          if (t === tile) return;
          var showing = open.indexOf(t) > -1 ||
            (t.style.visibility !== 'hidden' && parseFloat(t.style.opacity || 0) > 0.01);
          if (!showing) return;
          var other = t.querySelector('img');
          if (other) taken.push(other.getAttribute('src'));
        });
        var free = pool.filter(function (u) { return taken.indexOf(u) < 0; });
        if (!free.length) return;
        var src = gsap.utils.random(free);
        if (img.getAttribute('src') !== src) img.setAttribute('src', src);
      }

      var open = [];
      function show(tile) {
        fill(tile);                          /* while it is still invisible */
        if (open.indexOf(tile) < 0) open.push(tile);
        g.place(open, true, tile);
      }
      function hide(tile) {
        var i = open.indexOf(tile);
        if (i > -1) open.splice(i, 1);
        g.place(open, false, tile);
      }

      /* start from the closed layout without animating into it */
      g.place(open, false);
      gsap.killTweensOf(g.items);
      gsap.killTweensOf(g.tiles);
      var gap0 = g.gap(), before0 = [], coll0 = 0;
      g.items.forEach(function (el) {
        before0.push(coll0);
        if (el.classList.contains('klr-tile')) coll0 += el.offsetWidth + gap0;
      });
      g.items.forEach(function (el, i) { gsap.set(el, { x: coll0 / 2 - before0[i] }); });

      /* Random, per tile — no shared timeline, so no pattern. */
      var MAX_VISIBLE = 2;             /* hard rule: never a third photo on screen */
      var WAIT = [1.1, 5.2];           /* seconds between a tile's turns */
      var STAY = [1.6, 3.8];           /* seconds a tile stays up */
      var rand = gsap.utils.random;

      /* How many tiles may be up at once. The node draws two, but the title has
         to FIT: a long series name (SIGNATURE, ENSEMBLE) or a narrower laptop
         leaves room for only one, and a row wider than its container is clipped
         by the hero's overflow — which reads as a badly centred title. So the
         cap is measured, never assumed. Re-measured per turn because the type
         is fluid (clamp on vw), so the numbers change as the window does. */
      function capacity() {
        var gap = g.gap(), words = 0, tileW = 0, tiles = 0;
        g.items.forEach(function (el) {
          if (el.classList.contains('klr-tile')) { tileW = Math.max(tileW, el.offsetWidth); tiles++; }
          else { words += el.offsetWidth; }
        });
        if (!tileW) return 1;
        var room = root.clientWidth - words - gap * (g.items.length - tiles - 1);
        return Math.max(1, Math.min(MAX_VISIBLE, Math.floor(room / (tileW + gap))));
      }

      /* Photos actually ON SCREEN right now — counted off the elements, so it
         includes a tile that is still fading out as well as one that is fading
         in. capacity() caps how many may be OPEN; this caps what the eye can
         see, which is the rule the design asks for (Rene, 2026-08-22). The two
         can differ for a fraction of a second around a handover, and it is that
         fraction this closes. */
      function visible() {
        var n = 0;
        for (var i = 0; i < g.tiles.length; i++) {
          var t = g.tiles[i];
          if (t.style.visibility !== 'hidden' && parseFloat(t.style.opacity || 0) > 0.01) n++;
        }
        return n;
      }
      /* one live handle per tile, replaced each turn — the loop runs for as long
         as the page is open, so keeping every spent delayedCall would pile up */
      var live = g.tiles.map(function () { return null; });

      function after(tile, delay, fn) {
        var i = g.tiles.indexOf(tile);
        if (live[i]) live[i].kill();
        live[i] = gsap.delayedCall(delay, fn);
      }

      /* True while a tile is closed on paper but still fading off screen. The
         layout targets are computed from the open set, so a tile arriving in
         that window would be placed into space the fading one still occupies.
         Read straight off the elements — no bookkeeping to get out of step. */
      function fading() {
        for (var i = 0; i < g.tiles.length; i++) {
          var t = g.tiles[i];
          if (open.indexOf(t) < 0 && t.style.visibility !== 'hidden' &&
              parseFloat(t.style.opacity || 0) > 0.02) return true;
        }
        return false;
      }

      function roll(tile, first) {
        after(tile, first ? rand(0.4, 2.6) : rand(WAIT[0], WAIT[1]), function () {
          if (visible() >= MAX_VISIBLE || fading() ||
              open.length >= capacity() || open.indexOf(tile) > -1) {
            roll(tile);               /* not this tile's moment — roll again */
            return;
          }
          show(tile);
          after(tile, rand(STAY[0], STAY[1]), function () {
            hide(tile);
            roll(tile);
          });
        });
      }
      g.tiles.forEach(function (tile) { roll(tile, true); });

      return function () {
        live.forEach(function (c) { c && c.kill(); });
        gsap.set(g.items, { clearProps: 'x' });
        gsap.set(g.tiles, { clearProps: 'transform,opacity,visibility' });
      };
    }

    /* ---------- reduced motion: the design's resting state ---------- */
    function still(root) {
      var g = build(root);
      if (!g.tiles.length) return null;
      var open = g.tiles.slice(0, 2);
      gsap.set(g.tiles, { scale: 0, autoAlpha: 0 });
      var gap = g.gap();
      var before = [], collapsed = 0;
      g.items.forEach(function (el) {
        before.push(collapsed);
        if (el.classList.contains('klr-tile') && open.indexOf(el) < 0) collapsed += el.offsetWidth + gap;
      });
      var centre = collapsed / 2;
      g.items.forEach(function (el, i) { gsap.set(el, { x: centre - before[i] }); });
      gsap.set(open, { scale: 1, autoAlpha: 1 });
      return function () {
        gsap.set(g.items, { clearProps: 'x' });
        gsap.set(g.tiles, { clearProps: 'transform,opacity,visibility' });
      };
    }
  }

  if (window.gsap) init();
  else window.addEventListener('load', init);
})();
